import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import {
  BindFormFieldChecked,
  BindFormSelectValue,
  BindFormFieldProp,
  BindFormFieldValue,
  BindRequiredValue,
  FormError,
  FormField,
  FormHook,
  FormValue,
  GetErrorProps,
  UseFormProps,
  BindFormSelectFieldProp,
} from './model';
import { convertStringInFields, isFormError, isFormField } from './helper';

export const useForm = <T>({
  defaultValues,
  submitOnEnter,
  onSubmit,
  onChangeValidator,
  onSubmitValidatior,
  i18nFn,
  disabledFields,
  enabledFields,
  requiredFields,
  requiredValidationOptions,
}: UseFormProps<T>): FormHook<T> => {
  //----------STATE----------//
  const [form, setForm] = useState<T>({} as T);

  const [errors, setErrs] = useState<FormError<T>[]>([]);

  const [disableds, setDisabledFields] = useState<FormField<T>[]>([]);

  const [enableds, setEnabledFields] = useState<FormField<T>[]>([]);

  const [requireds, setRequiredFields] = useState<FormField<T>[]>([]);
  //----//

  //-----------INIT---------//
  useEffect(() => {
    if (defaultValues) {
      setForm(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);
  //----//

  //-----------VALUE HANDLERS---------//
  const getValue = useCallback(
    ({ field, path }: FormField<T>) => {
      return path ? _.get(form, [field, ...path]) : _.get(form, field);
    },
    [form],
  );

  const setValue = useCallback(({ field, value, path }: FormValue<T>) => {
    setForm((prev) => {
      let formTemp = _.cloneDeep(prev) as object;
      if (!path) {
        _.set(formTemp, field, value);
      } else {
        _.set(formTemp, [field, ...path], value);
      }
      return formTemp as T;
    });
  }, []);

  const setValues = useCallback((vals: FormValue<T>[]) => {
    setForm((prev) => {
      let formTemp = _.cloneDeep(prev) as object;

      vals.forEach((el) => {
        if (el.path) {
          _.set(formTemp, [el.field, ...el.path], el.value);
        } else {
          _.set(formTemp, el.field, el.value);
        }
      });

      return formTemp as T;
    });
  }, []);
  //----//

  //-----------REQUIRED FIELDS HANDLERS-------------//
  useEffect(() => {
    if (requiredFields) {
      setRequiredFields(convertStringInFields(requiredFields));
    } else {
      setRequiredFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredFields?.length]);

  const isRequired = useCallback(
    (field: FormField<T>) => {
      let a = _.find(requireds, { ...field });
      return isFormField(a);
    },
    [requireds],
  );

  const bindRequired = useCallback(
    (field: FormField<T>): BindRequiredValue => {
      if (isRequired(field)) {
        return {
          InputLabelProps: {
            required: true,
            sx: {
              '& .MuiInputLabel-asterisk': {
                color: 'secondary.main',
                backgroundColor: 'common.white',
                fontWeight: '600',
                paddingRight: '10px',
              },
            },
          },
        };
      } else {
        return { InputLabelProps: undefined };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [errors, isRequired],
  );
  //----//

  //-----------DISABLED FIELDS HANDLERS-------------//
  useEffect(() => {
    if (disabledFields) {
      setDisabledFields(convertStringInFields(disabledFields));
    } else {
      setDisabledFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledFields?.length]);

  useEffect(() => {
    if (enabledFields) {
      setEnabledFields(convertStringInFields(enabledFields));
    } else {
      setEnabledFields([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledFields?.length]);

  const isDisabled = useCallback(
    (field: FormField<T>) => {
      if (disableds.length > 0) {
        let a = _.find(disableds, { ...field });
        return isFormField(a);
      } else {
        if (enableds.length > 0) {
          let a = _.find(enableds, { ...field });
          return !isFormField(a);
        } else {
          return false;
        }
      }
    },
    [disableds, enableds],
  );
  //----//

  //-----------ERROR HANDLING-----------//

  const getError = useCallback(({ errors, field, path }: GetErrorProps<T>): FormError<T> | undefined => {
    let errorsTemp = [...errors];
    let err = _.find(errorsTemp, { field: field, path: path });
    if (isFormError<T>(err)) {
      return err;
    }
  }, []);

  const setError = useCallback(
    ({ error, field, path }: FormError<T>) => {
      setErrs((prev) => {
        let errorsTemp = [...prev];
        let err = getError({ errors: prev, field: field, path: path });
        if (err) {
          //rimuovo l'errore se presente
          let errs = _.reject(errorsTemp, { field: field, path: path });
          if (errs && errs.length > 0 && isFormError(errs[0])) {
            errorsTemp = errs as FormError<T>[];
          } else {
            errorsTemp = [];
          }
        }
        errorsTemp.push({
          field: field,
          path: path,
          error: error,
        });

        return errorsTemp;
      });
    },
    [getError],
  );

  const setErrors = useCallback((errors: FormError<T>[]) => {
    setErrs(errors);
  }, []);

  const removeError = useCallback(
    ({ field, path }: FormField<T>) => {
      setErrs((prev) => {
        let errorsTemp = [...prev];
        let err = getError({ errors: errorsTemp, field: field, path: path });
        if (err) {
          let errs = _.reject(errorsTemp, { field: field, path: path });
          if (errs && errs.length > 0 && isFormError(errs[0])) {
            errorsTemp = errs as FormError<T>[];
          } else {
            errorsTemp = [];
          }
        }
        return errorsTemp;
      });
    },
    [getError],
  );

  const bindError = useCallback(
    ({ field, path }: FormField<T>) => {
      let error = getError({ errors: errors, field: field, path: path });

      let helperText = error?.error;

      let isError = helperText !== undefined;

      if (error && isError && i18nFn) {
        helperText = i18nFn(error);
      }

      return {
        helperText: helperText,
        error: isError,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18nFn, errors, getError],
  );
  //----//

  //----------ONCHANGE-------//
  useEffect(() => {
    if (form) {
      let errs: FormError<T>[] = [];
      if (onChangeValidator) {
        errs = onChangeValidator(form);
      }
      if (requiredValidationOptions && requiredValidationOptions.validationTime === 'ON_CHANGE') {
        requireds.forEach((element) => {
          let val = getValue(element);
          if (val === '' || val === undefined || val === null) {
            errs.push({
              field: element.field,
              path: element.path,
              error:
                requiredValidationOptions.errorMsg ?? 'Message must be specified in requiredValidationOptions.errorMsg',
            });
          }
        });
      }
      setErrors(errs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, getValue, onChangeValidator, requireds, setErrors]);
  //----//

  //---------SUBMIT---------//
  const submit = useCallback(() => {
    if (onSubmit) {
      let errs: FormError<T>[] = [];
      if (onSubmitValidatior && onSubmitValidatior(form).length > 0) {
        setErrs(onSubmitValidatior(form));
      }
      if (requiredValidationOptions && requiredValidationOptions.validationTime === 'ON_SUBMIT') {
        requireds.forEach((element) => {
          let val = getValue(element);
          if (val === '' || val === undefined || val === null) {
            errs.push({
              field: element.field,
              path: element.path,
              error:
                requiredValidationOptions.errorMsg ?? 'Message must be specified in requiredValidationOptions.errorMsg',
            });
          }
        });
      }
      if (errs.length === 0) {
        return onSubmit(form);
      } else {
        setErrors(errs);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, getValue, onSubmit, onSubmitValidatior, requireds, setErrors]);
  //----//

  //-----------FROM BINDING---------------//

  const keyDownHandler = useCallback(
    <E>(e: React.KeyboardEvent<E>) => {
      if (submitOnEnter) {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          submit();
        }
      }
    },
    [submit, submitOnEnter],
  );

  const bindTextField = useCallback(
    ({
      field,
      defaultVal,
      disabled,
      path,
      helperText,
      disableSubmitOnEnter,
    }: BindFormFieldProp<T>): BindFormFieldValue => {
      let name = field as string;
      let value: any = getValue({ field: field }) ?? defaultVal;
      let onChange = (e: any) => {
        removeError({ field: field, path: path });
        setValue({ field: field, value: e.target.value });
      };

      if (path) {
        name = `${field as string}_${path.join('_')}`;
        value = getValue({ field: field, path: path }) ?? defaultVal;
        onChange = (e: any) => {
          removeError({ field: field, path: path });
          setValue({ field: field, value: e.target.value, path: path });
        };
      }

      const error = bindError({ field: field, path: path });

      const { InputLabelProps } = bindRequired({ field: field, path: path });

      return {
        name: name,
        value: value ?? '',
        onChange: onChange,
        disabled: disabled ?? isDisabled({ field: field, path: path }),
        error: error.error,
        helperText: error.error ? error.helperText : helperText,
        InputLabelProps: InputLabelProps,
        onKeyDown: disableSubmitOnEnter ? undefined : keyDownHandler,
      };
    },
    [getValue, bindError, bindRequired, isDisabled, keyDownHandler, removeError, setValue],
  );

  const bindSelect = useCallback(
    ({
      field,
      defaultVal,
      disabled,
      path,
      helperText,
      multiple,
      disableSubmitOnEnter,
    }: BindFormSelectFieldProp<T>): BindFormSelectValue => {
      let name = field as string;
      let value: any = getValue({ field: field }) ?? defaultVal;
      let onChange = (e: any) => {
        removeError({ field: field, path: path });
        setValue({ field: field, value: e.target.value });
      };

      if (path) {
        name = `${field as string}_${path.join('_')}`;
        value = getValue({ field: field, path: path }) ?? defaultVal;
        onChange = (e: any) => {
          removeError({ field: field, path: path });
          setValue({ field: field, value: e.target.value, path: path });
        };
      }

      const error = bindError({ field: field, path: path });

      const { InputLabelProps } = bindRequired({ field: field, path: path });

      return {
        select: true,
        name: name,
        disabled: disabled ?? isDisabled({ field: field, path: path }),
        error: error.error,
        helperText: error.error ? error.helperText : helperText,
        SelectProps: {
          multiple: multiple ?? false,
          value: multiple ? value ?? [] : value ?? '',
          onChange: onChange,
        },
        InputLabelProps: InputLabelProps,
        onKeyDown: disableSubmitOnEnter ? undefined : keyDownHandler,
      };
    },
    [getValue, bindError, bindRequired, isDisabled, keyDownHandler, removeError, setValue],
  );

  const bindDatePicker = useCallback(
    ({
      field,
      defaultVal,
      disabled,
      path,
      helperText,
      disableSubmitOnEnter,
    }: BindFormFieldProp<T>): BindFormFieldValue => {
      let name = field as string;
      let value: any = getValue({ field: field }) ?? defaultVal;
      let onChange = (e: any) => {
        removeError({ field: field, path: path });
        setValue({ field: field, value: e });
      };

      if (path) {
        name = `${field as string}_${path.join('_')}`;
        value = getValue({ field: field, path: path }) ?? defaultVal;
        onChange = (e: any) => {
          removeError({ field: field, path: path });
          setValue({ field: field, value: e, path: path });
        };
      }

      const error = bindError({ field: field, path: path });

      const { InputLabelProps } = bindRequired({ field: field, path: path });

      return {
        name: name,
        value: value ?? new Date(),
        onChange: onChange,
        disabled: disabled ?? isDisabled({ field: field, path: path }),
        error: error.error,
        helperText: error.error ? error.helperText : helperText,
        InputLabelProps: InputLabelProps,
        onKeyDown: disableSubmitOnEnter ? undefined : keyDownHandler,
      };
    },
    [getValue, bindError, bindRequired, isDisabled, keyDownHandler, removeError, setValue],
  );

  const bindCheckbox = useCallback(
    ({
      field,
      defaultVal,
      disabled,
      path,
      helperText,
      disableSubmitOnEnter,
    }: BindFormFieldProp<T>): BindFormFieldChecked => {
      let name = field as string;
      let value: any = getValue({ field: field }) ?? defaultVal;
      let onChange = () => {
        removeError({ field: field, path: path });
        setValue({ field: field, value: !value });
      };

      if (path) {
        name = `${field as string}_${path.join('_')}`;
        value = getValue({ field: field, path: path }) || defaultVal;
        onChange = () => {
          removeError({ field: field, path: path });
          setValue({ field: field, value: !value, path: path });
        };
      }

      const error = bindError({ field: field, path: path });

      const { InputLabelProps } = bindRequired({ field: field, path: path });

      return {
        name: name,
        checked: value ?? false,
        onChange: onChange,
        disabled: disabled ?? isDisabled({ field: field, path: path }),
        error: error.error,
        helperText: error.error ? error.helperText : helperText,
        InputLabelProps: InputLabelProps,
        onKeyDown: disableSubmitOnEnter ? undefined : keyDownHandler,
      };
    },
    [getValue, bindError, bindRequired, isDisabled, keyDownHandler, removeError, setValue],
  );
  //----//

  return {
    form,
    errors,
    setValue,
    setValues,
    bindCheckbox,
    bindTextField,
    bindSelect,
    bindDatePicker,
    bindError,
    setErrors,
    setError,
    removeError,
    submit,
    isDisabled,
    isRequired,
    bindRequired,
  };
};
